import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaCheck, FaTimes } from "react-icons/fa";
import { useTournamentAuth } from "../../utils/authorization";
import InlineAlert from "../UI/DesignStandards/Notifications/InlineAlert";
import {
  PrimaryButton,
  SecondaryButton,
} from "../UI/DesignStandards";
import {
  buildPayoutTableFromRecipe,
  recipeForLoadedTable,
  recipeToPayoutTableColumns,
} from "../../utility/generateShapedPayoutTable";
import PayoutTableWorkspace from "../organizations/PayoutTables/PayoutTableWorkspace";
import { RoundWholeDollarsSwitch } from "../organizations/PayoutTables/PayoutOptionsPanel";
import { usePayoutWorkspace } from "../organizations/PayoutTables/usePayoutWorkspace";
import { useTournamentPayoutTable } from "./hooks/useTournamentPayoutTable";
import PayoutTableSkeleton from "./components/PayoutTableSkeleton";

const selectClass =
  "w-full max-w-md px-3 py-2 border border-brand-charcoal/20 rounded-lg bg-white text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold-ink disabled:opacity-60";

const snapshotOf = (payoutData, advanced, planning, cellsEdited) => ({
  payoutData: payoutData.map((row) => ({ ...row })),
  advanced: { ...advanced },
  planning: { ...planning },
  cellsEdited,
});

const TournamentPayoutTable = ({ tournamentId, embedded = false }) => {
  const { canManage } = useTournamentAuth(tournamentId);
  const {
    loading,
    error,
    setError,
    table,
    loadedPayoutData,
    organizationTables,
    defaultPayoutTableId,
    isPayoutFinalized,
    isFrozenCopy,
    isFollowingOrganization,
    assignTable,
    saveCurrent,
  } = useTournamentPayoutTable(tournamentId);

  const canEdit = canManage && !isPayoutFinalized && Boolean(table);
  const workspace = usePayoutWorkspace({ canEdit });
  const {
    showMore,
    setShowMore,
    advanced,
    planning,
    payoutData,
    cellsEdited,
    isEditing,
    isRecommended,
    optionsLocked,
    allColumnsValid,
    calculateColumnTotal,
    isColumnValid,
    hydrate,
    handlePercentageChange,
    handlePlanningField,
    handleRoundWholeDollars,
    handleAdvancedField,
    handleResetRecommended,
    handleAddColumn,
    handleDeleteColumn,
    handleStartEditCells,
    handleFinishEditing,
    handleCancelEditing,
  } = workspace;

  const [saving, setSaving] = useState(false);
  const [baseline, setBaseline] = useState(null);

  useEffect(() => {
    if (!table) {
      hydrate();
      setBaseline(null);
      return;
    }
    const recipe = recipeForLoadedTable(table, loadedPayoutData);
    const nextData =
      loadedPayoutData.length > 0
        ? loadedPayoutData.map((row) => ({ ...row }))
        : buildPayoutTableFromRecipe(recipe.options, recipe.planning);
    hydrate({
      options: recipe.options,
      planning: recipe.planning,
      payoutData: nextData,
      cellsEdited: recipe.cellsEdited,
    });
    setBaseline(
      snapshotOf(nextData, recipe.options, recipe.planning, recipe.cellsEdited)
    );
    // Hydrate only when this tournament points at a different table.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table?.id, hydrate]);

  const dirty = useMemo(() => {
    if (!baseline) return false;
    return (
      JSON.stringify(payoutData) !== JSON.stringify(baseline.payoutData) ||
      JSON.stringify(advanced) !== JSON.stringify(baseline.advanced) ||
      JSON.stringify(planning) !== JSON.stringify(baseline.planning) ||
      cellsEdited !== baseline.cellsEdited
    );
  }, [advanced, baseline, cellsEdited, payoutData, planning]);

  const organizationTableOptions = useMemo(() => {
    if (!table || isFrozenCopy) return organizationTables;
    if (organizationTables.some((orgTable) => orgTable.id === table.id)) {
      return organizationTables;
    }
    return [{ id: table.id, name: table.name }, ...organizationTables];
  }, [isFrozenCopy, organizationTables, table]);

  const handlePickTable = async (value) => {
    if (!value || value === "frozen") return;
    setError("");
    try {
      await assignTable(value);
    } catch (err) {
      console.error(err);
      setError("Could not assign that payout table.");
    }
  };

  const handleSave = async () => {
    if (!canEdit || !allColumnsValid) return;
    setSaving(true);
    setError("");
    try {
      await saveCurrent({
        name: table.name,
        description: table.description,
        payoutData,
        recipe: recipeToPayoutTableColumns({
          options: advanced,
          planning,
          cellsEdited,
        }),
      });
      handleFinishEditing();
      setBaseline(snapshotOf(payoutData, advanced, planning, cellsEdited));
    } catch (err) {
      console.error(err);
      setError(err.message || "Could not save this payout table.");
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (!table || !baseline) {
      handleCancelEditing();
      return;
    }
    hydrate({
      options: baseline.advanced,
      planning: baseline.planning,
      payoutData: baseline.payoutData,
      cellsEdited: baseline.cellsEdited,
    });
  };

  const statusCopy = () => {
    if (isPayoutFinalized) {
      return "Payouts are locked. This is the chart this event paid from.";
    }
    if (isFrozenCopy) {
      return "This event has its own copy. Unlock did not snap back to the organization table. Pick an organization table to follow it again.";
    }
    if (isFollowingOrganization) {
      return "This tournament follows the organization table. Edits here change that table for other unlocked events too.";
    }
    return "Pick an organization table. New events follow the default until you choose another.";
  };

  if (loading) {
    return <PayoutTableSkeleton />;
  }

  const selectValue = isFrozenCopy ? "frozen" : table?.id || "";

  return (
    <div className="flex flex-col gap-4 sm:gap-6 min-w-0">
      {canManage || !embedded ? (
      <header className="card-board overflow-hidden">
        {embedded ? null : (
          <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
            <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
              Payout table
            </h1>
            <p className="text-sm text-brand-mist mt-1">{statusCopy()}</p>
          </div>
        )}
        <div className="p-4 sm:p-6 space-y-4">
          {embedded ? (
            <p className="text-sm text-brand-muted">{statusCopy()}</p>
          ) : null}
          <div className="flex flex-col lg:flex-row lg:items-end gap-4">
            <div className="flex-1">
              <label
                htmlFor="tournament-payout-table"
                className="block text-sm font-medium text-brand-charcoal mb-1"
              >
                Organization table
              </label>
              <select
                id="tournament-payout-table"
                value={selectValue}
                disabled={!canManage || isPayoutFinalized}
                onChange={(e) => handlePickTable(e.target.value)}
                className={selectClass}
              >
                {!table ? <option value="">Select a table</option> : null}
                {isFrozenCopy ? (
                  <option value="frozen">This event's copy</option>
                ) : null}
                {organizationTableOptions.map((orgTable) => (
                  <option key={orgTable.id} value={orgTable.id}>
                    {orgTable.name || "Payout table"}
                    {orgTable.id === defaultPayoutTableId ? " (default)" : ""}
                  </option>
                ))}
              </select>
              {canManage && table ? (
                <p className="text-sm text-brand-muted mt-2">
                  Directors edit these tables in{" "}
                  <Link
                    to="/organization/payout-tables"
                    className="text-brand-charcoal hover:underline"
                  >
                    organization payout tables
                  </Link>
                  .
                </p>
              ) : null}
              {table ? (
                <div className="mt-4">
                  <RoundWholeDollarsSwitch
                    checked={planning.roundToWholeDollars !== false}
                    onChange={handleRoundWholeDollars}
                    disabled={!canEdit}
                  />
                </div>
              ) : null}
            </div>
            {canEdit && (dirty || isEditing) ? (
              <div className="flex flex-wrap gap-2">
                <PrimaryButton
                  onClick={handleSave}
                  icon={FaCheck}
                  disabled={saving || (dirty && !allColumnsValid)}
                >
                  {saving ? "Saving..." : "Save"}
                </PrimaryButton>
                <SecondaryButton
                  onClick={handleDiscard}
                  icon={FaTimes}
                  className="!px-3 !py-2 text-sm"
                >
                  Cancel
                </SecondaryButton>
              </div>
            ) : null}
          </div>
        </div>
      </header>
      ) : null}

      {error ? (
        <InlineAlert type="error" title="There was a problem" message={error} />
      ) : null}
      {canEdit && dirty && !allColumnsValid ? (
        <InlineAlert
          type="warning"
          title="Columns must total 100%"
          message="Every places-paid column has to add to 100% before you can save."
        />
      ) : null}

      {table ? (
        <PayoutTableWorkspace
          canEdit={canEdit}
          showMore={showMore}
          onToggleMore={() => setShowMore((open) => !open)}
          places={advanced.places}
          shape={advanced.shape}
          ratioNumerator={advanced.ratioNumerator}
          ratioDenominator={advanced.ratioDenominator}
          payoutData={payoutData}
          planning={planning}
          onPlanningChange={handlePlanningField}
          isEditing={isEditing}
          onChangeField={handleAdvancedField}
          isRecommended={isRecommended}
          onResetRecommended={handleResetRecommended}
          optionsLocked={optionsLocked}
          onEditCells={handleStartEditCells}
          onSave={handleSave}
          onCancel={handleCancelEditing}
          saving={saving}
          canSave={!dirty || allColumnsValid}
          calculateColumnTotal={calculateColumnTotal}
          isColumnValid={isColumnValid}
          onPercentageChange={handlePercentageChange}
          onAddColumn={handleAddColumn}
          onDeleteColumn={handleDeleteColumn}
          idPrefix="tournament-payout"
        />
      ) : (
        <div className="card-board p-6 text-brand-muted">
          {canManage
            ? "Pick an organization table to see the split."
            : "A payout table has not been posted yet."}
        </div>
      )}
    </div>
  );
};

export default TournamentPayoutTable;
